// frontend/src/components/onboarding/create-link-dialog.tsx
// New Onboarding Link dialog, shared by the Onboarding page and the Customers
// table. Onboarding links belong to CUSTOMERS (never team workspaces): the
// assignment section is customer-labeled, and a presetCustomer (from the
// Customers table) pins the target and hides the section entirely.
// Title Case button labels; no em-dashes.
import { useEffect, useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { useParams } from 'react-router';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFetch } from '@/hooks/use-fetch';
import { parseApiError } from '@/lib/api-error';
import { FieldError } from '@/components/form/field-error';
import { useFacebookEnabled, useInstagramEnabled } from '@/hooks/use-app-config';
import { optionalHttpsUrlSchema } from '@/lib/form/schemas';

/** Channel options. Single source for the base-ui Select `items` (so the
 * trigger renders the label, not the raw value) and the rendered SelectItems. */
const CHANNEL_ITEMS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
] as const;

type LinkChannelType = (typeof CHANNEL_ITEMS)[number]['value'];

/** Customer-assignment options. Same single-source pattern as CHANNEL_ITEMS. */
const ATTACH_ITEMS = [
  { value: 'new', label: 'New Customer' },
  { value: 'existing', label: 'Existing Customer' },
] as const;

/** Mint payload from POST create. The link stays re-displayable afterwards. */
export interface MintedLink {
  publicId: string;
  url: string;
  verifyToken: string;
}

/** Picker option from GET /organizations/:orgId/workspaces?kind=customer. */
interface CustomerOption {
  publicId: string;
  name: string;
}

/** An existing link's editable fields — prefills the dialog in edit mode. */
export interface EditableLink {
  publicId: string;
  channelType: LinkChannelType;
  label: string;
  externalId: string | null;
  destinationWebhookUrl: string | null;
  destinationVerifyTokenOverride: string | null;
  successRedirectUrl: string | null;
  failureRedirectUrl: string | null;
}

interface CreateLinkValues {
  channelType: LinkChannelType;
  label: string;
  attachMode: 'new' | 'existing';
  workspaceName: string;
  targetWorkspaceId: string;
  externalId: string;
  destinationWebhookUrl: string;
  destinationVerifyToken: string;
  successRedirectUrl: string;
  failureRedirectUrl: string;
}

function initialCreateValues(): CreateLinkValues {
  return {
    channelType: 'whatsapp',
    label: '',
    attachMode: 'new',
    workspaceName: '',
    targetWorkspaceId: '',
    externalId: '',
    destinationWebhookUrl: '',
    destinationVerifyToken: '',
    successRedirectUrl: '',
    failureRedirectUrl: '',
  };
}

function validateOptionalHttpsUrl(value: string): string | undefined {
  const parsed = optionalHttpsUrlSchema.safeParse(value.trim());
  return parsed.success
    ? undefined
    : (parsed.error.issues[0]?.message ?? 'Enter a valid URL');
}

interface CreateLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** From the Customers table: pins the target customer and hides the
   * Customer Assignment section (the customer is already chosen). */
  presetCustomer?: CustomerOption | null;
  /** When set, the dialog edits this link in place (PATCH) instead of creating
   * one: fields prefill, the channel + assignment are locked, and the token is
   * preserved (the connect URL does not change). */
  editLink?: EditableLink | null;
  /** Called after a successful create or edit (parent refreshes its list). */
  onSaved: () => void | Promise<void>;
}

export function CreateLinkDialog({
  open,
  onOpenChange,
  presetCustomer = null,
  editLink = null,
  onSaved,
}: CreateLinkDialogProps) {
  const { orgId } = useParams<{ orgId: string }>();
  const fetchWithAuth = useFetch();
  const instagramEnabled = useInstagramEnabled();
  const facebookEnabled = useFacebookEnabled();
  // Hide a flagged-off option, except when viewing an existing link of that
  // type (the select is disabled in edit mode and must keep rendering the
  // link's real type).
  const enabled: Record<LinkChannelType, boolean> = {
    whatsapp: true,
    instagram: instagramEnabled,
    facebook: facebookEnabled,
  };
  const channelItems = CHANNEL_ITEMS.filter((i) => enabled[i.value] || editLink?.channelType === i.value);

  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [creating, setCreating] = useState(false);
  const [testState, setTestState] = useState<'idle' | 'testing' | 'ok' | 'fail'>('idle');
  const [testError, setTestError] = useState('');
  // The url+token combo that last passed the test; editing either field after a
  // pass invalidates it (currentSig stops matching), forcing a re-test.
  const [testedSig, setTestedSig] = useState('');

  const createForm = useForm({
    defaultValues: initialCreateValues(),
    onSubmit: async ({ value }) => {
      if (editLink) await handleUpdate(value);
      else await handleCreate(value);
    },
  });

  // Edit mode: prefill from the link and pre-satisfy the destination test for
  // the unchanged saved destination (it passed at create time). setFieldValue,
  // not reset(): reset doesn't repaint already-mounted controlled inputs.
  useEffect(() => {
    if (!open || !editLink) return;
    createForm.setFieldValue('channelType', editLink.channelType);
    createForm.setFieldValue('label', editLink.label);
    createForm.setFieldValue('externalId', editLink.externalId ?? '');
    createForm.setFieldValue('destinationWebhookUrl', editLink.destinationWebhookUrl ?? '');
    createForm.setFieldValue('destinationVerifyToken', editLink.destinationVerifyTokenOverride ?? '');
    createForm.setFieldValue('successRedirectUrl', editLink.successRedirectUrl ?? '');
    createForm.setFieldValue('failureRedirectUrl', editLink.failureRedirectUrl ?? '');
    if (editLink.destinationWebhookUrl) {
      setTestState('ok');
      setTestedSig(`${editLink.destinationWebhookUrl}\n${editLink.destinationVerifyTokenOverride ?? ''}`);
    } else {
      setTestState('idle');
      setTestedSig('');
    }
  }, [open, editLink, createForm]);

  // Load the customer picker only when the section is shown (no preset).
  useEffect(() => {
    if (!open || presetCustomer || !orgId) return;
    let cancelled = false;
    void (async () => {
      // Onboarding links target customers; the picker offers customers only
      // (backend rejects a team target at create + finalize).
      const res = await fetchWithAuth(`/organizations/${orgId}/workspaces?kind=customer`);
      if (res.ok && !cancelled) {
        const data = (await res.json()) as { workspaces: CustomerOption[] };
        setCustomers(data.workspaces.map((w) => ({ publicId: w.publicId, name: w.name })));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, presetCustomer, orgId, fetchWithAuth]);

  function resetForm() {
    createForm.reset(initialCreateValues());
    setTestState('idle');
    setTestError('');
    setTestedSig('');
  }

  async function handleCreate(value: CreateLinkValues) {
    if (!value.label.trim()) return;
    setCreating(true);
    try {
      const payload: Record<string, string> = {
        channelType: value.channelType,
        label: value.label.trim(),
      };
      if (presetCustomer) {
        payload.targetWorkspaceId = presetCustomer.publicId;
      } else if (value.attachMode === 'existing' && value.targetWorkspaceId) {
        payload.targetWorkspaceId = value.targetWorkspaceId;
      } else if (value.attachMode === 'new' && value.workspaceName.trim()) {
        payload.workspaceName = value.workspaceName.trim();
      }
      if (value.externalId.trim()) payload.externalId = value.externalId.trim();
      if (value.destinationWebhookUrl.trim()) {
        payload.destinationWebhookUrl = value.destinationWebhookUrl.trim();
        payload.destinationVerifyTokenOverride = value.destinationVerifyToken.trim();
      }
      if (value.successRedirectUrl.trim()) payload.successRedirectUrl = value.successRedirectUrl.trim();
      if (value.failureRedirectUrl.trim()) payload.failureRedirectUrl = value.failureRedirectUrl.trim();

      const res = await fetchWithAuth('/org/onboarding-links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        onOpenChange(false);
        resetForm();
        toast.success('Link created');
        await onSaved();
      } else {
        const body = await res.json().catch(() => null);
        toast.error(parseApiError(res.status, body, 'Failed to create link').userMessage);
      }
    } catch {
      toast.error('Failed to create link');
    } finally {
      setCreating(false);
    }
  }

  // Edit mode: PATCH the editable fields in place (token/URL preserved). Every
  // field is sent so clearing an optional URL persists (backend maps '' → null).
  async function handleUpdate(value: CreateLinkValues) {
    if (!editLink || !value.label.trim()) return;
    setCreating(true);
    try {
      const payload: Record<string, string> = {
        label: value.label.trim(),
        externalId: value.externalId.trim(),
        destinationWebhookUrl: value.destinationWebhookUrl.trim(),
        successRedirectUrl: value.successRedirectUrl.trim(),
        failureRedirectUrl: value.failureRedirectUrl.trim(),
      };
      if (value.destinationWebhookUrl.trim()) {
        payload.destinationVerifyTokenOverride = value.destinationVerifyToken.trim();
      }
      const res = await fetchWithAuth(`/org/onboarding-links/${editLink.publicId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        onOpenChange(false);
        resetForm();
        toast.success('Link updated');
        await onSaved();
      } else {
        const body = await res.json().catch(() => null);
        toast.error(parseApiError(res.status, body, 'Failed to update link').userMessage);
      }
    } catch {
      toast.error('Failed to update link');
    } finally {
      setCreating(false);
    }
  }

  // Live verify handshake against the override destination. On success the
  // current url+token combo is remembered (testedSig) so Create unlocks until
  // either field changes.
  async function handleTestDestination() {
    const url = createForm.state.values.destinationWebhookUrl.trim();
    const verifyToken = createForm.state.values.destinationVerifyToken.trim();
    if (validateOptionalHttpsUrl(url)) return;
    if (!url || !verifyToken) return;
    setTestState('testing');
    setTestError('');
    try {
      const res = await fetchWithAuth('/org/onboarding-links/test-destination', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, verifyToken }),
      });
      const body = (await res.json().catch(() => null)) as
        | { success?: boolean; error?: string }
        | null;
      if (res.ok && body?.success) {
        setTestState('ok');
        setTestedSig(`${url}\n${verifyToken}`);
      } else {
        setTestState('fail');
        setTestError(
          body?.error ?? parseApiError(res.status, body, 'Test failed').userMessage,
        );
      }
    } catch {
      setTestState('fail');
      setTestError('Could not reach the destination.');
    }
  }

  // Create gating: with no override URL the link inherits the org default and
  // Create is free; with an override the url+token combo must have passed Test.
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="pr-6 leading-snug">
            {editLink
              ? presetCustomer
                ? `Edit Link For ${presetCustomer.name}`
                : 'Edit Onboarding Link'
              : presetCustomer
                ? `New Link For ${presetCustomer.name}`
                : 'New Onboarding Link'}
          </DialogTitle>
        </DialogHeader>
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void createForm.handleSubmit();
          }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label>Channel</Label>
            <createForm.Field name="channelType">
              {(field) => (
                <Select
                  items={channelItems}
                  value={field.state.value}
                  disabled={!!editLink}
                  onValueChange={(v) => field.handleChange(v as LinkChannelType)}
                >
                  <SelectTrigger aria-label="Channel">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {channelItems.map((i) => (
                      <SelectItem key={i.value} value={i.value}>
                        {i.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </createForm.Field>
          </div>

          <div className="space-y-2">
            <Label htmlFor="onb-label">Link Name</Label>
            <createForm.Field name="label">
              {(field) => (
                <Input
                  id="onb-label"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="Acme onboarding"
                  autoFocus
                />
              )}
            </createForm.Field>
          </div>

          {/* Customer assignment — hidden when the customer is preset (Customers
              table scoped it) or in edit mode (a link can't be reassigned). */}
          {!presetCustomer && !editLink && (
            <>
              <div className="space-y-2">
                <Label>Customer Assignment</Label>
                <createForm.Field name="attachMode">
                  {(field) => (
                    <Select
                      items={ATTACH_ITEMS}
                      value={field.state.value}
                      onValueChange={(v) => field.handleChange(v as 'new' | 'existing')}
                    >
                      <SelectTrigger aria-label="Customer Assignment">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ATTACH_ITEMS.map((i) => (
                          <SelectItem key={i.value} value={i.value}>
                            {i.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </createForm.Field>
              </div>

              <createForm.Subscribe selector={(state) => state.values.attachMode}>
                {(attachMode) =>
                  attachMode === 'new' ? (
                    <createForm.Field name="workspaceName">
                      {(field) => (
                        <div className="space-y-2">
                          <Label htmlFor="onb-workspace-name">Customer Name (optional)</Label>
                          <Input
                            id="onb-workspace-name"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            placeholder="Acme WhatsApp"
                          />
                        </div>
                      )}
                    </createForm.Field>
                  ) : (
                    <createForm.Field name="targetWorkspaceId">
                      {(field) => (
                        <div className="space-y-2">
                          <Label>Customer</Label>
                          <Select
                            items={customers.map((w) => ({
                              value: w.publicId,
                              label: w.name,
                            }))}
                            value={field.state.value}
                            onValueChange={(v) => field.handleChange(v ?? '')}
                          >
                            <SelectTrigger aria-label="Customer">
                              <SelectValue placeholder="Select a customer" />
                            </SelectTrigger>
                            <SelectContent>
                              {customers.map((w) => (
                                <SelectItem key={w.publicId} value={w.publicId}>
                                  {w.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </createForm.Field>
                  )
                }
              </createForm.Subscribe>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="onb-external-id">External ID (optional)</Label>
            <createForm.Field name="externalId">
              {(field) => (
                <Input
                  id="onb-external-id"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="your-customer-id"
                />
              )}
            </createForm.Field>
          </div>

          <div className="space-y-2">
            <Label htmlFor="onb-destination">Inbound Destination URL (optional)</Label>
            <createForm.Field
              name="destinationWebhookUrl"
              validators={{
                onChange: ({ value }) => validateOptionalHttpsUrl(value),
                onSubmit: ({ value }) => validateOptionalHttpsUrl(value),
              }}
            >
              {(field) => {
                const hasError = field.state.meta.errors.length > 0;
                return (
                  <>
                    <Input
                      id="onb-destination"
                      type="url"
                      value={field.state.value}
                      onChange={(e) => {
                        field.handleChange(e.target.value);
                        setTestState('idle');
                        setTestError('');
                        setTestedSig('');
                      }}
                      onBlur={field.handleBlur}
                      placeholder="https://api.example.com/webhooks/inbound"
                      aria-invalid={hasError || undefined}
                      aria-describedby={hasError ? 'onb-destination-error' : undefined}
                    />
                    <FieldError id="onb-destination-error" errors={field.state.meta.errors} />
                  </>
                );
              }}
            </createForm.Field>
          </div>

          <createForm.Subscribe
            selector={(state) => ({
              destinationWebhookUrl: state.values.destinationWebhookUrl,
              destinationVerifyToken: state.values.destinationVerifyToken,
            })}
          >
            {({ destinationWebhookUrl, destinationVerifyToken }) => {
              const destUrlTrim = destinationWebhookUrl.trim();
              const destTokenTrim = destinationVerifyToken.trim();
              const destUrlError = validateOptionalHttpsUrl(destUrlTrim);
              const destTestPassed =
                testState === 'ok' && testedSig === `${destUrlTrim}\n${destTokenTrim}`;
              if (destUrlTrim === '') return null;
              return (
                <div className="space-y-2">
                  <Label htmlFor="onb-verify-token">Verify Token</Label>
                  <div className="flex items-center gap-2">
                    <createForm.Field name="destinationVerifyToken">
                      {(field) => (
                        <Input
                          id="onb-verify-token"
                          value={field.state.value}
                          onChange={(e) => {
                            field.handleChange(e.target.value);
                            setTestState('idle');
                            setTestError('');
                            setTestedSig('');
                          }}
                          onBlur={field.handleBlur}
                          placeholder="token your endpoint echoes back"
                          className="flex-1"
                        />
                      )}
                    </createForm.Field>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => void handleTestDestination()}
                      disabled={!destTokenTrim || !!destUrlError || testState === 'testing'}
                    >
                      {testState === 'testing' && (
                        <Loader2 className="size-3.5 animate-spin" />
                      )}
                      Test
                    </Button>
                  </div>
                  {destTestPassed ? (
                    <p
                      role="status"
                      aria-live="polite"
                      className="flex items-center gap-1.5 text-xs text-green-600"
                    >
                      <CheckCircle2 className="size-3.5" />
                      Destination verified.
                    </p>
                  ) : testState === 'fail' ? (
                    <p role="alert" className="flex items-center gap-1.5 text-xs text-destructive">
                      <XCircle className="size-3.5" />
                      {testError || 'Test failed.'}
                    </p>
                  ) : (
                    <p role="status" aria-live="polite" className="text-xs text-muted-foreground">
                      Test the destination before creating the link.
                    </p>
                  )}
                </div>
              );
            }}
          </createForm.Subscribe>

          <div className="space-y-2">
            <Label htmlFor="onb-success">Success Redirect URL (optional)</Label>
            <createForm.Field
              name="successRedirectUrl"
              validators={{
                onChange: ({ value }) => validateOptionalHttpsUrl(value),
                onSubmit: ({ value }) => validateOptionalHttpsUrl(value),
              }}
            >
              {(field) => {
                const hasError = field.state.meta.errors.length > 0;
                return (
                  <>
                    <Input
                      id="onb-success"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      placeholder="https://app.example.com/connected"
                      aria-invalid={hasError || undefined}
                      aria-describedby={hasError ? 'onb-success-error' : undefined}
                    />
                    <FieldError id="onb-success-error" errors={field.state.meta.errors} />
                  </>
                );
              }}
            </createForm.Field>
          </div>

          <div className="space-y-2">
            <Label htmlFor="onb-failure">Failure Redirect URL (optional)</Label>
            <createForm.Field
              name="failureRedirectUrl"
              validators={{
                onChange: ({ value }) => validateOptionalHttpsUrl(value),
                onSubmit: ({ value }) => validateOptionalHttpsUrl(value),
              }}
            >
              {(field) => {
                const hasError = field.state.meta.errors.length > 0;
                return (
                  <>
                    <Input
                      id="onb-failure"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      placeholder="https://app.example.com/connect-failed"
                      aria-invalid={hasError || undefined}
                      aria-describedby={hasError ? 'onb-failure-error' : undefined}
                    />
                    <FieldError id="onb-failure-error" errors={field.state.meta.errors} />
                    <p className="text-xs text-muted-foreground">
                      We&apos;ll send your customer here if connecting fails, with status=failed
                      and a reason code.
                    </p>
                  </>
                );
              }}
            </createForm.Field>
          </div>

          <createForm.Subscribe
            selector={(state) => {
              const values = state.values;
              const destUrlTrim = values.destinationWebhookUrl.trim();
              const destTokenTrim = values.destinationVerifyToken.trim();
              const destTestPassed =
                testState === 'ok' && testedSig === `${destUrlTrim}\n${destTokenTrim}`;
              const destReady = destUrlTrim === '' || destTestPassed;
              const hasUrlError =
                !!validateOptionalHttpsUrl(values.destinationWebhookUrl) ||
                !!validateOptionalHttpsUrl(values.successRedirectUrl) ||
                !!validateOptionalHttpsUrl(values.failureRedirectUrl);
              const missingCustomer =
                !presetCustomer &&
                !editLink &&
                values.attachMode === 'existing' &&
                !values.targetWorkspaceId;
              return {
                disabled:
                  creating ||
                  !values.label.trim() ||
                  !destReady ||
                  hasUrlError ||
                  missingCustomer,
              };
            }}
          >
            {({ disabled }) => (
              <Button
                type="submit"
                className="w-full"
                disabled={disabled}
              >
                {creating && <Loader2 className="size-3.5 animate-spin" />}
                {editLink ? 'Save Changes' : 'Create Link'}
              </Button>
            )}
          </createForm.Subscribe>
        </form>
      </DialogContent>
    </Dialog>
  );
}
